import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getMarketplaceListings, startMarketplaceOAuth, startMarketplaceDeleteOAuth } from '../services/api';
import '../styles/marketplace.css';

function fmtPrice(listing, t) {
    if (listing.negotiable) return t('marketplace.negotiableLabel');
    if (listing.price) return `R$ ${parseFloat(listing.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    return null;
}

function fmtDate(dt) {
    if (!dt) return '';
    return new Date(dt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function PhotoCarousel({ images, onOpenLightbox }) {
    const [idx, setIdx] = useState(0);
    if (!images || images.length === 0) return null;
    const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); };
    const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); };
    return (
        <div className="mp-carousel">
            <img src={images[idx].image_url} alt="" onClick={() => onOpenLightbox(images, idx)} />
            {images.length > 1 && (
                <>
                    <button className="mp-carousel-btn mp-carousel-prev" onClick={prev}>‹</button>
                    <button className="mp-carousel-btn mp-carousel-next" onClick={next}>›</button>
                    <span className="mp-carousel-count">{idx + 1}/{images.length}</span>
                </>
            )}
        </div>
    );
}

function Lightbox({ images, startIdx, onClose }) {
    const [idx, setIdx] = useState(startIdx);
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') setIdx(i => (i - 1 + images.length) % images.length);
            if (e.key === 'ArrowRight') setIdx(i => (i + 1) % images.length);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [images.length, onClose]);
    return (
        <div className="mp-lightbox" onClick={onClose}>
            <button className="mp-lightbox-close" onClick={onClose}>✕</button>
            <img src={images[idx].image_url} alt="" onClick={e => e.stopPropagation()} />
            {images.length > 1 && (
                <>
                    <button className="mp-carousel-btn mp-carousel-prev mp-lb-btn" onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }}>‹</button>
                    <button className="mp-carousel-btn mp-carousel-next mp-lb-btn" onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }}>›</button>
                    <span className="mp-lightbox-count">{idx + 1} / {images.length}</span>
                </>
            )}
        </div>
    );
}

export default function Marketplace() {
    const { t } = useTranslation();
    usePageTitle('Marketplace');
    useCanonicalTag();
    useMetaTags({
      title: 'Marketplace | Celeritas',
      description: 'Buy and sell sim racing gear on the Celeritas marketplace. Find the best deals in the community.',
      url: 'https://celeritas.live/marketplace',
    });
    const [searchParams] = useSearchParams();
    const [listings, setListings] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [lightbox, setLightbox] = useState(null); // {images, idx}
    const [oauthLoading, setOauthLoading] = useState(false);
    const [oauthError, setOauthError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    async function handleDelete(itemId) {
        if (!window.confirm(t('marketplace.deleteConfirm'))) return;
        setDeletingId(itemId);
        try {
            const data = await startMarketplaceDeleteOAuth(itemId);
            window.location.href = data.oauth_url;
        } catch {
            setDeletingId(null);
        }
    }
    const error = searchParams.get('marketplace_error');
    const deleteSuccess = searchParams.get('delete_success');

    const load = useCallback((pg) => {
        setLoading(true);
        getMarketplaceListings({ page: pg })
            .then(data => { setListings(data.results); setCount(data.count); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(1); }, [load]);

    async function handleCreate() {
        setOauthLoading(true);
        setOauthError(null);
        try {
            const data = await startMarketplaceOAuth();
            window.location.href = data.oauth_url;
        } catch (e) {
            setOauthLoading(false);
            setOauthError(t('marketplace.oauthStartError'));
        }
    }

    const totalPages = Math.ceil(count / 20);

    return (
        <div className="App">
            <Header />
            {lightbox && (
                <Lightbox images={lightbox.images} startIdx={lightbox.idx} onClose={() => setLightbox(null)} />
            )}
            <main className="main-content">
                <div className="marketplace-page">
                    <div className="marketplace-hero">
                        <div>
                            <h1>{t('marketplace.pageTitle')}</h1>
                            <p>{t('marketplace.pageSubtitle')}</p>
                        </div>
                        <button className="btn-create" onClick={handleCreate} disabled={oauthLoading}>
                            {oauthLoading ? '...' : `+ ${t('marketplace.createBtn')}`}
                        </button>
                    </div>

                    {deleteSuccess && (
                        <div style={{ background: '#1e2d1e', border: '1px solid #3a5a3a', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#4caf50', fontSize: '0.88rem' }}>
                            ✓ {t('marketplace.deleteSuccess')}
                        </div>
                    )}

                    {oauthError && (
                        <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '10px 16px', marginBottom: 16, fontSize: '0.85rem' }}>
                            {oauthError}
                        </div>
                    )}

                    {error && (
                        <div style={{ background: '#1e2a1e', border: '1px solid #3a5a3a', borderRadius: 8, padding: '20px 24px', marginBottom: 24 }}>
                            <div style={{ fontWeight: 700, color: '#e07070', marginBottom: 6, fontSize: '0.95rem' }}>
                                {t('marketplace.errorTitle')}
                            </div>
                            <div style={{ color: '#9ba3ab', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                {t('marketplace.errorBody')}
                            </div>
                            <button
                                onClick={handleCreate}
                                style={{ marginTop: 14, background: '#ff4747', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                            >
                                {t('marketplace.errorRetry')}
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <p style={{ color: '#9ba3ab' }}>{t('common.loading')}</p>
                    ) : listings.length === 0 ? (
                        <p style={{ color: '#9ba3ab' }}>{t('marketplace.noListings')}</p>
                    ) : (
                        <div className="marketplace-grid">
                            {listings.map(item => {
                                const price = fmtPrice(item, t);
                                return (
                                    <div key={item.id} className="marketplace-card">
                                        <div className="mp-card-left">
                                            <div className="mp-card-header">
                                                <span className="mp-card-seller">✓ {item.display_name}</span>
                                                <span className="mp-card-sep">·</span>
                                                <span className="mp-card-title">{item.title}</span>
                                            </div>
                                            <div className="mp-card-custid">iRacing ID: {item.cust_id}</div>
                                            <div className="mp-card-desc">{item.description}</div>
                                            {(item.country || item.state || item.city) && (
                                                <div className="mp-card-location">
                                                    📍 {[item.city, item.state, item.country].filter(Boolean).join(', ')}
                                                </div>
                                            )}
                                            {price && <div className="mp-card-price">{price}</div>}
                                            {(item.contact_email || item.contact_phone || item.contact_discord) && (
                                                <>
                                                    <div className="mp-card-contacts-label">{t('marketplace.contactsLabel')}</div>
                                                    <div className="mp-card-contacts">
                                                        {item.contact_email && <span>✉ {item.contact_email}</span>}
                                                        {item.contact_phone && <span>📱 {item.contact_phone}</span>}
                                                        {item.contact_discord && <span><i className="fab fa-discord" style={{marginRight:4}}/>{t('marketplace.discordIdLabel')}: {item.contact_discord}</span>}
                                                    </div>
                                                </>
                                            )}
                                            <div className="mp-card-date">{fmtDate(item.created_at)}</div>
                                        </div>
                                        <div className="mp-card-right">
                                            {item.images && item.images.length > 0 && (
                                                <PhotoCarousel
                                                    images={item.images}
                                                    onOpenLightbox={(imgs, idx) => setLightbox({ images: imgs, idx })}
                                                />
                                            )}
                                            <p className="mp-card-delete-hint">{t('marketplace.deleteHint')}</p>
                                            <button
                                                className="mp-card-delete-btn"
                                                onClick={() => handleDelete(item.id)}
                                                disabled={deletingId === item.id}
                                            >
                                                {deletingId === item.id ? t('common.loading') : t('marketplace.deleteBtn')}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="marketplace-pagination">
                            <button disabled={page <= 1} onClick={() => { setPage(p => p - 1); load(page - 1); }}>
                                {t('marketplace.prev')}
                            </button>
                            <span style={{ color: '#9ba3ab', fontSize: '0.85rem' }}>{page} / {totalPages}</span>
                            <button disabled={page >= totalPages} onClick={() => { setPage(p => p + 1); load(page + 1); }}>
                                {t('marketplace.next')}
                            </button>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
